
import engine from '../engine';
import Scene3D from 'apollo-e/Scene3D';
import ColoredObject from 'apollo-e/visuals/ColoredObject';
import QTContainer3D from '../components/QTContainer3D';
import QT3D from '../components/QT3D';
import BitmapText3D from '../components/BitmapText3D';
import GameObject from 'apollo-e/GameObject';

const backgroundMissingStartJ = 20;
const backgroundMissingAtJ = 100;
const backgroundMissingAtJChance = 0.1;
const backgroundMissingK = -Math.log(1 - backgroundMissingAtJChance) / (backgroundMissingAtJ - backgroundMissingStartJ);
const backgroundTileHideOffset = 1e7;
const uint32Max = 4294967295;
const DISABLE_BACKGROUND_TILES = false;

const backgroundMissingChance = (j: number) => {
  if (j < backgroundMissingStartJ) {
    return 0;
  }

  return 1 - Math.exp(-backgroundMissingK * (j - backgroundMissingStartJ));
};

const backgroundTileRandom = (i: number, j: number) => {
  let h = Math.imul(i, 374761393) ^ Math.imul(j, 668265263);
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return h / uint32Max;
};

class Tileset {
  minI: number;
  maxI: number;
  minJ: number;
  maxJ: number;

  width: number;
  height: number;

  tiles: Array<Array<any>>;

  add: () => any;
  remove: (object: any) => void;
  reposition: (object: any, i: number, j: number, width: number, height: number) => void;

  constructor (width, height, add, remove, reposition) {
    this.minI = -Infinity;
    this.maxI = Infinity;
    this.minJ = -Infinity;
    this.maxJ = Infinity;


    this.width = width;
    this.height = height;

    this.tiles = [];

    this.add = add;
    this.remove = remove;
    this.reposition = reposition;
  }

  update (minX, maxX, minY, maxY) {
    const firstI = Math.max(this.minI, Math.floor(minX / this.width));
    const firstJ = Math.max(this.minJ, Math.floor(minY / this.height));

    const lastI = Math.min(this.maxI, Math.ceil(maxX / this.width));
    const lastJ = Math.min(this.maxJ, Math.ceil(maxY / this.height));

    const tileCount = Math.max(0, lastJ - firstJ + 1) * Math.max(0, lastI - firstI + 1);

    // adding necessary tiles
    for (let t = this.tiles.length; t < tileCount; t++) {
      this.tiles[t] = this.add();
    }

    // removing unnecessary tiles
    for (let t = tileCount; t < this.tiles.length; t++) {
      this.remove(this.tiles[t]);
    }

    // if (!isFinite(tileCount)) {
    //   debugger
    // }
    this.tiles.length = tileCount;

    let tileIndex = 0;
    for (let i = firstI; i <= lastI; i++) {
      for (let j = firstJ; j <= lastJ; j++) {
        this.reposition(this.tiles[tileIndex], i, j, this.width, this.height);
        tileIndex += 1;
      }
    }
  }
}

export default class Scroller {
  arcadeScene: Scene3D;
  depth: number;

  grassTileset: Tileset;
  backgroundTileset: Tileset | null;
  groudChunkTileset: Tileset;

  minX: number;
  maxX: number;
  minY: number;
  maxY: number;

  constructor (textures, arcadeScene: Scene3D, depth: number) {

    this.arcadeScene = arcadeScene;
    this.depth = depth;

    // const logoDepthStart = -depth + 200;
    // const logoDepthEnd = -depth + 3;
    // const logoCount = 6;

    // for (let l = 0; l < logoCount; l ++) {
    //   const gameLogo = new QT3D();
    //   gameLogo.textureSet = textures.gameLogoBlack;
    //   gameLogo.castShadow = false;
    //   gameLogo.transform.position.x = 0;
    //   gameLogo.transform.position.y = 450;
    //   gameLogo.transform.position.z = logoDepthEnd + (logoDepthStart - logoDepthEnd) * l / (logoCount - 1);
    //   gameLogo.transform.scales.x = 600 * 1.1;
    //   gameLogo.transform.scales.y = 292 * 1.1;
    //   gameLogo.transform.pivot = 0.5;
    //   arcadeScene.addObject(gameLogo);
    // }
  
    // const happyTechLogo = new QT3D();
    // happyTechLogo.textureSet = textures.happyTechLogo;
    // happyTechLogo.castShadow = true;
    // happyTechLogo.transform.position.x = 290;
    // happyTechLogo.transform.position.y = -410;
    // happyTechLogo.transform.position.z = -depth + 200;
    // happyTechLogo.transform.scales.x = 140;
    // happyTechLogo.transform.scales.y = 140;
    // happyTechLogo.transform.pivot = 0.5;
    // arcadeScene.addObject(happyTechLogo);

    // Add BitmapText3D with "Dolly\n#{n}" where n is seconds since Dec 1, 2025
    const dollyText = new BitmapText3D();
    dollyText.bitmapText.font = 'mplus';
    dollyText.bitmapText.size = 30;
    dollyText.bitmapText.multiline = true;
    dollyText.castShadow = true;
    dollyText.transform.position.x = 195;
    dollyText.transform.position.y = -380;
    // Slightly in front of background tiles to avoid z-fighting at larger camera distances.
    dollyText.transform.position.z = -depth + 5; // Behind the sheep
    dollyText.transform.scale = 1.0;

    // const dec1_2025 = new Date(2025, 11, 18, 0, 0, 0).getTime(); // Month is 0-indexed, so 11 = December
    // const now = Date.now();
    // const secondsSinceDec1 = Math.floor((now - dec1_2025) / 5_000);
    dollyText.bitmapText.string = `To the\nMoon!`;

    // Force layout computation to create the characters
    dollyText.bitmapText.computeLayout();

    // Set text color to grey
    dollyText.setAllCharactersColor(0.2, 0.2, 0.2, 1.0);

    arcadeScene.addObject(dollyText);

    const controlHint = new BitmapText3D();
    controlHint.bitmapText.font = 'mplus';
    controlHint.bitmapText.size = 22;
    controlHint.bitmapText.multiline = true;
    controlHint.castShadow = true;
    controlHint.transform.position.x = -100;
    controlHint.transform.position.y = -300;
    // Slightly in front of background tiles to avoid z-fighting at larger camera distances.
    controlHint.transform.position.z = -depth + 5; // Behind the sheep
    controlHint.transform.scale = 1.0;
    controlHint.bitmapText.string = 'meeèh!';
    controlHint.bitmapText.computeLayout();

    controlHint.setAllCharactersColor(0.9, 0.4, 0.0, 1.0);

    arcadeScene.addObject(controlHint);

    this.grassTileset = new Tileset(250, 20, () => {
      const grass = new ColoredObject();
      grass.color.r = 0.28;
      grass.color.g = 0.82;
      grass.color.b = 0.33;
      grass.castShadow = true;
    
      const grassTransform = grass.transform;
      grassTransform.position.z = -125;
      grassTransform.scales.x = 250;
      grassTransform.scales.y = 20;
      grassTransform.scales.z = 250;
      arcadeScene.addObject(grass);

      return grass;
    }, (grass) => {
      arcadeScene.removeObject(grass);
    }, (grass, i, j, width, height) => {
      const grassTransform = grass.transform;
      grassTransform.position.x = (i + 0.5) * width;
      grassTransform.position.y = j * height;
    });

    this.grassTileset.minJ = -26;
    this.grassTileset.maxJ = -26;

    this.backgroundTileset = null;
    if (!DISABLE_BACKGROUND_TILES) {
      const backgroundTiles = new QTContainer3D(['position']);
      backgroundTiles.textureSet = textures.tile;
      arcadeScene.addObject(backgroundTiles);

      const tileDim = 250;
      this.backgroundTileset = new Tileset(tileDim, tileDim, () => {
        const tile = backgroundTiles.createParticle();
        tile.width = tileDim;
        tile.height = tileDim;
        // tile.pivot = 0.5;
        tile.position.z = -depth;
        tile.greyShade = 1;

        return tile;
      }, (tile) => {
        backgroundTiles.removeParticle(tile);
      }, (tile, i, j, width, height) => {
        const missingChance = backgroundMissingChance(j);
        const random = backgroundTileRandom(i, j);
        if (random < missingChance) {
          tile.position.x = backgroundTileHideOffset;
          tile.position.y = backgroundTileHideOffset;
          return;
        }

        tile.position.x = (i - 0.5) * width;
        tile.position.y = (j - 1) * height;
      });
    }

    const groundChunks = new QTContainer3D(['position', 'rotation'], 200);
    groundChunks.textureSet = textures.ground;
    arcadeScene.addObject(groundChunks);

    const groundChunkDim = 100;
    this.groudChunkTileset = new Tileset(groundChunkDim, groundChunkDim, () => {
      const chunk = groundChunks.createParticle();
      chunk.width = groundChunkDim;
      chunk.height = groundChunkDim;
      chunk.pivot = 0.5;
      chunk.position.z = 0;
      // chunk.rotation = Math.floor(Math.random() * 4) * Math.PI / 2;

      return chunk;
    }, (chunk) => {
      groundChunks.removeParticle(chunk);
    }, (chunk, i, j, width, height) => {
      chunk.position.x = i * width;
      chunk.position.y = j * height + 20;
      chunk.rotation = Math.floor(i + j) * Math.PI / 2;
    });

    this.groudChunkTileset.maxJ = -6;

    this.minX = 0;
    this.maxX = 0;
    this.minY = 0;
    this.maxY = 0;
  }

  update () {
    const scene = this.arcadeScene;
    const camera = scene.camera;
    const height = 2 * camera.getHeight(camera.position.z + this.depth);
    const width = height * engine.renderer.width / engine.renderer.height;

    const x = camera.position.x;
    const y = camera.position.y;

    this.minX = x - width / 2;
    this.maxX = x + width / 2;
    this.minY = y - height / 2;
    this.maxY = y + height / 2;

    this.grassTileset.update(this.minX, this.maxX, this.minY, this.maxY);
    if (this.backgroundTileset) {
      this.backgroundTileset.update(this.minX, this.maxX, this.minY, this.maxY);
    }
    this.groudChunkTileset.update(this.minX, this.maxX, this.minY, this.maxY);
  }
}
